import React from 'react';
import { Save, CheckCircle2 } from 'lucide-react';
import { ToolbarButton } from '@/components/shared/ToolbarButton';
import { cn } from '@/lib/utils';

/**
 * Save / Update action for the posture app shell (S3 / U2).
 *
 * Two defects motivated pulling this out of `posture-analysis-app.tsx`:
 *
 * 1. Success was conveyed by the green background **alone**. The label that would have said
 *    "Saved!" was inside `hidden sm:inline`, so on a phone — where practitioners actually work —
 *    a colour-blind user got no confirmation at all (WCAG 1.4.1).
 * 2. Nothing announced the transition, so screen-reader users never learned the save completed.
 *
 * Now three independent channels carry it: the icon swaps to a tick, the label stays in the
 * accessibility tree (`sr-only` below `sm`, visible from `sm` up) as an `aria-live` region, and
 * colour is the redundant channel. Visible text is hidden on narrow viewports so the practitioner
 * toolbar fits without clipping.
 */
export type SaveAnalysisStatus = 'idle' | 'saving' | 'saved';

export interface SaveAnalysisButtonProps {
  status: SaveAnalysisStatus;
  /** True when overwriting an existing saved analysis rather than creating one. */
  isUpdate: boolean;
  onClick: () => void;
  className?: string;
}

export const saveAnalysisLabel = (status: SaveAnalysisStatus, isUpdate: boolean): string => {
  if (status === 'saving') return 'Saving...';
  if (status === 'saved') return isUpdate ? 'Updated!' : 'Saved!';
  return isUpdate ? 'Update' : 'Save';
};

export const SaveAnalysisButton: React.FC<SaveAnalysisButtonProps> = ({
  status,
  isUpdate,
  onClick,
  className,
}) => {
  const label = saveAnalysisLabel(status, isUpdate);
  const saved = status === 'saved';

  return (
    <ToolbarButton
      onClick={onClick}
      isPrimary
      disabled={status === 'saving'}
      data-testid="save-analysis-button"
      aria-label={label}
      title={label}
      className={cn('transition-colors duration-200', saved && 'bg-success hover:bg-success/90', className)}
    >
      {saved ? (
        <CheckCircle2 className="h-4 w-4 sm:mr-2" aria-hidden="true" />
      ) : (
        <Save className="h-4 w-4 sm:mr-2" aria-hidden="true" />
      )}
      <span className="hidden sm:inline" aria-hidden="true">{label}</span>
      <span className="sr-only sm:hidden" aria-live="polite">{label}</span>
    </ToolbarButton>
  );
};

export default SaveAnalysisButton;
